import Layout from '../../../components/layout';
import DetailBase from '../../../components/detailBase/detailBase';
import styled from 'styled-components';
import Page from '../../../components/page';
import useWindowSize from '../../../hooks/windowsSize';
import React, { useMemo } from 'react';
import DetailMap from '../../../components/detailBase/map/map';
import { gql, useQuery } from '@apollo/client';
import { GET_APPS } from '../../../sharedQueries/getApps';
import { GQLTour } from 'townapps-models/index';
import useScreenView from '../../../hooks/useScreenView';
const HorizontalCardsDetail = dynamic(() => import('../../../components/horizontalCardsDetail'), { ssr: false });
import { useRouter } from 'next/router';
import NotFound from '../../../components/404';
import { NextSeo } from 'next-seo';
import isEmpty from 'lodash/isEmpty';
import { Gallery } from '../../../components/detailBase/listingDetailBody';
import client from '../../../utils/apolloClient';
import ClientOnly from '../../../components/clientOnly';
import dynamic from 'next/dynamic';
import useLogView from '../../../hooks/useLogView';
import { Types } from '../../../utils/Types';
import { imageDefaultShare } from '../../../utils/defaultImageShare';
const Attractions = dynamic(() => import('../../../components/_attractions'));

const GET_TOUR = gql`
  query getItem($ID: String, $type: String) {
    tour: getItemByID(ID: $ID, type: $type) {
      ... on Tour {
        id
        title
        isDraft
        latitude
        longitude
        description
        image
        gallery
        distance
        time
        dataset
        attractions
        slug
        blurhash
        fbOgImage
        appId
        type
        app {
          id
          name
          colour
          slug
        }
      }
    }
  }
`;

const GET_ATTRACTIONS = gql`
  query getAttractions($appId: String!, $ids: [String]) {
    attractions: attraction(appId: $appId, ids: $ids, limit: 30) {
      id
      title
      description
      latitude
      longitude
      slug
      fbOgImage
      blurhash
      website
      address
      phone
      email
      image
      oldId
      tagsObject
      type
      app {
        id
        name
        colour
        slug
      }
    }
  }
`;

const GET_EXTRAS = gql`
  query Items($appId: String!) {
    extras: tour(appId: $appId, withDrafts: false) {
      id
      title
      description
      type
      latitude
      longitude
      distance
      time
      image
      slug
      blurhash
      fbOgImage
      isDraft
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
`;

const ItemText = styled.p`
  width: 100%;
  font-size: 16px;
  text-align: justify;
  margin: 32px 0;
  white-space: pre-line;
`;

const TourExtras: React.FC<any> = ({ viewport, item, size }) => {
  const extras = useQuery(GET_ATTRACTIONS, { variables: { appId: item?.appId, ids: item?.attractions.map(i => i.toString()) } });
  const attractions = extras?.data?.attractions ?? [];

  const geojson = useMemo(() => {
    if (!extras.data) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    return {
      type: 'FeatureCollection',
      features: extras.data.attractions.map(i => ({
        type: 'Feature',
        properties: {
          image: i.image,
          title: i.title,
          id: i.id,
          type: i.__typename,
          itemType: i.type,
          slug: i.slug,
        },
        geometry: {
          type: 'Point',
          coordinates: [i.longitude, i.latitude],
        },
      })),
    };
  }, [extras]);
  return (
    <>
      <DetailMap viewport={viewport} geojson={geojson} dataset={item?.dataset} />
      <Attractions size={size} attractions={attractions} />
    </>
  );
};

const Body = (item, size, viewport) => {
  const img = item?.image ?? '/placeholderImage.png';

  return (
    <Wrapper>
      <Gallery gallery={!isEmpty(item?.gallery) ? [img, ...item.gallery] : [img]} item={item} />
      <ItemText>{item?.description}</ItemText>
      {item && (
        <ClientOnly>
          <TourExtras size={size} item={item} viewport={viewport} />
        </ClientOnly>
      )}
    </Wrapper>
  );
};

const TourDetailPage = ({ tour, preview }) => {
  const item = tour;
  const size = useWindowSize();
  const router = useRouter();
  useLogView(item?.id, item?.appId, Types.Tour, preview);

  useScreenView(tour ? tour.title : null);

  const viewport = { latitude: tour?.latitude, longitude: tour?.longitude, zoom: 13 };
  const body = Body(item, size, viewport);

  const isFallBack = router.isFallback;
  if (isFallBack) {
    return (
      <Page>
        <DetailBase backgroundImage={'/tourBackground.jpeg'} blurHash={'LhIrZwNGR.s:?dIpogobtPV@oga}'} fallBack />
      </Page>
    );
  }

  if (!item || (item.isDraft && !preview)) {
    if (!item) {
      return (
        <Page>
          <DetailBase backgroundImage={'/tourBackground.jpeg'} blurHash={'LhIrZwNGR.s:?dIpogobtPV@oga}'} fallBack />
        </Page>
      );
    } else {
      return (
        <>
          <NextSeo
            title={item?.title}
            facebook={{
              appId: '507054450225530',
            }}
            openGraph={{
              title: item?.title,
              description:
                item && (item.description || (item as any).excerpt) ? `${(item?.description || (item as any)?.excerpt).slice(0, 155)}...` : '',
              url: `https://xploreapp.io${router.asPath}`,
              images: [
                {
                  url: item && item.fbOgImage ? item.fbOgImage : imageDefaultShare,
                  width: 1200,
                  height: 650,
                  alt: `${item?.title} image`,
                },
              ],
            }}
            twitter={{
              handle: '@handle',
              site: '@site',
              cardType: 'summary_large_image',
            }}
          />
          <NotFound />
        </>
      );
    }
  }

  return (
    <Page noMarginTop>
      <Layout>
        <DetailBase
          item={item}
          withDownload
          shareOnTitle
          isTrailWalk
          Body={body}
          backgroundImage={'/tourBackground.jpeg'}
          blurHash={'LhIrZwNGR.s:?dIpogobtPV@oga}'}
        />
      </Layout>

      {tour && (
        <ClientOnly>
          <HorizontalCardsDetail gqlQuery={GET_EXTRAS} appID={tour.appId} slugApp={tour.app.slug} />
        </ClientOnly>
      )}
    </Page>
  );
};

const GET_LFSTYLES = gql`
  query getWalks($appId: String) {
    tours: tour(appId: $appId) {
      id
      title
      description
      image
      latitude
      longitude
      slug
      blurhash
      fbOgImage
    }
  }
`;

export async function getStaticPaths() {
  const apolloClient = client;
  const { data } = await apolloClient.query({ query: GET_APPS });
  const paths = [];
  for (const app of data.apps.filter(a => a.cards)) {
    const res = await apolloClient.query<{ tours: GQLTour[] }>({
      query: GET_LFSTYLES,
      variables: { appId: app.id },
    });
    const items = res.data.tours;

    if (items && items.length)
      for (const item of items) {
        paths.push({ params: { slug: app.slug, id: item?.slug ? item.slug : item.id } });
      }
  }

  return {
    paths: paths,
    fallback: true,
  };
}

export async function getStaticProps(ctx) {
  const id = ctx.params.id;
  const apolloClient = client;

  try {
    const res = await apolloClient.query({ query: GET_TOUR, variables: { ID: id, type: 'Tour' } });

    if (ctx.preview) {
      return {
        props: { tour: res.data.tour, preview: true },
      };
    } else {
      return {
        props: { tour: res.data.tour },
        revalidate: 9600,
      };
    }
  } catch (error) {
    return {
      props: { tour: null },
    };
  }
}

export default TourDetailPage;
